/** @jsx jsx */
/** @jsx jsx */
import {
  jsx,
  Box,
  Button,
  Grid,
  Spinner,
  Container,
  useThemeUI,
} from "theme-ui";
import { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import donationsRecurringService from "sites-common/service-wrappers/donation-recurring-service";
import submitDonationRequest from "sites-common/service-wrappers/donation-service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePersist } from "../../../globals";
import Tos from "../../Tos";
import validateProfile from "./DonorProfile/validateProfile";
import AmountSelector from "./Amount/AmountSelector";
import ProfileSelector from "./DonorProfile/ProfileSelector";

import ErrorsOrWarnings from "./ErrorsOrWarnings";
import EditProfile from "./DonorProfile/EditProfile";
import useDonorProfiles from "../hooks/useDonorProfiles";
import { checkRestrictions, DonationConfProps } from "./restrictions";
import OnlineRecurringDonation, {
  useOnlyOnlineRecurringDonation,
} from "../../ui/Feature/RecurringDonations/OnlyOnline";
import useDonationConf from "../hooks/useDonationConf";
import OfflineRecurringDonations, {
  useOfflineDonation,
} from "../../ui/Feature/RecurringDonations/OnlyOffline";
import AllTypeRecurringDonation, {
  useMonthlyRecurringDonation,
} from "../../ui/Feature/RecurringDonations/AllType";
import ErrorsOrWarningsWrapper from "./ErrorsOrWarningsWrapper";
import RestrictionsWrapper from "./RestrictionsWrapper";

const getMandatoryEnv = require("sites-common/utils/getMandatoryEnv");

const { donationServiceConfig } = getMandatoryEnv(["donationServiceConfig"]);

const MONTHLY_RECURRING_DONATION_DEFAULT_STATE = {
  isMonthlyRecurringDonation: true,
  donation_type: "ONLINE",
  onlineSectionDetails: {
    account_type: "Savings",
    end_date: null,
    start_date: new Date(),
    untilCancelled: true,
  },
};

const OFFLINE_RECURRING_DONATION_DEFAULT_STATE = {
  showOfflineRecurringSection: true,
  donation_type: "OFFLINE",
  offlineSectionDetails: {
    payment_option:
      "Standing Instruction with donor’s bank for making recurring donations",
    standingInstructionDetails: {
      payment_mode: "Internet banking", // default value
      netBankingDetails: {
        donor_bank: "SELECT BANK", // default value
        standing_date: new Date(),
        donor_ifsc_code: "",
      },
      walletDetails: {
        wallet_mobile: "",
        standing_date: new Date(),
      },
    },
  },
};

const ALLTYPE_MONTHLY_RECURRING_DONATION_DEFAULT_STATE = {
  isMonthlyRecurringDonation: true,
  donation_type: "ONLINE",
  onlineSectionDetails: {
    account_type: "Savings",
    end_date: null,
    start_date: new Date(),
    untilCancelled: true,
  },
  offlineSectionDetails: {
    payment_option:
      "Standing Instruction with donor’s bank for making recurring donations",
    standingInstructionDetails: {
      payment_mode: "Internet banking",
      netBankingDetails: {
        donor_bank: "SELECT BANK", // default value
        standing_date: null,
        donor_ifsc_code: "",
      },
      walletDetails: {
        wallet_mobile: "",
        standing_date: null,
      },
    },
  },
};

const updateValidateState = (
  monthlyRecurringDonationState,
  offlineDonationRecurringState,
  allTypeRecurringState,
  donationConfDetails
) => {
  let monthlyDonationRecurringState = null;

  const { enableRecurringOffline, enableRecurring } = donationConfDetails;
  if (
    enableRecurringOffline &&
    enableRecurring &&
    allTypeRecurringState &&
    allTypeRecurringState.isMonthlyRecurringDonation
  ) {
    monthlyDonationRecurringState = allTypeRecurringState;
  } else if (
    enableRecurringOffline &&
    offlineDonationRecurringState &&
    offlineDonationRecurringState.showOfflineRecurringSection
  ) {
    monthlyDonationRecurringState = offlineDonationRecurringState;
  } else if (
    enableRecurring &&
    monthlyRecurringDonationState &&
    monthlyRecurringDonationState.isMonthlyRecurringDonation
  ) {
    monthlyDonationRecurringState = monthlyRecurringDonationState;
  }
  return { monthlyDonationRecurringState };
};

const doValidateSubmit = ({
  country,
  systemInfo,
  donorInfo,
  amount,
  currency,
  onErrors,
  onWarnings,
  setLoading,
  monthlyRecurringDonationState,
  offlineDonationRecurringState,
  allTypeRecurringState,
  onSuccess,
  overrideWarnings = [],
  donationConfDetails,
  conf,
}) => {
  const { monthlyDonationRecurringState } = updateValidateState(
    monthlyRecurringDonationState,
    offlineDonationRecurringState,
    allTypeRecurringState,
    donationConfDetails
  );

  const { errors, warnings } = validateProfile(
    country,
    donorInfo,
    amount,
    currency,
    false,
    monthlyDonationRecurringState
  );
  if (errors.length) {
    // console.error(errors);
    onErrors(errors);
    onWarnings(warnings);
    return;
  }

  if (warnings) {
    const pendingWarnings = warnings.filter(
      (w) => !overrideWarnings.includes(w)
    );
    if (pendingWarnings.length) {
      // console.warn(warnings);
      onWarnings(pendingWarnings);
      return;
    }
  }

  const formData = {
    donationServiceConfig,
    ...systemInfo,
    ...donorInfo,
    currency,
    amount,
  };

  const restrictions = checkRestrictions({
    conf,
    country,
    amount,
    donorInfo,
    donationId: systemInfo.donation_id,
  });

  if (restrictions.length) {
    return;
  }

  if (monthlyDonationRecurringState) {
    donationsRecurringService(
      formData,
      setLoading,
      (s) => onErrors([s]),
      monthlyDonationRecurringState
    );
  } else {
    submitDonationRequest(formData, setLoading, onSuccess, (s) =>
      onErrors([s])
    );
  }
};

const RecurringWidget = ({
  formDonationId,
  initialAmount,
  initialCurrency,
  projectId,
  packageDescription,
  country,
  initialProfile,
  enableRecurring,
  enableRecurringOffline,
  enableLastname,
  paymentSuccessUrl,
  paymentFailureUrl,
  showOtherCitizen,
  enableSignInOption,
  org,
  removeAccountType,
  meditationPlaceFieldAvl,
}) => {
  const { theme } = useThemeUI();
  const [to] = useState(initialCurrency);
  const [toAmount, setToAmount] = useState(initialAmount);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const isOffline = paymentSuccessUrl.includes("offline");

  const [monthlyRecurringDonationState, setMonthtlyRecurringDonationState] =
    useState(enableRecurring ? MONTHLY_RECURRING_DONATION_DEFAULT_STATE : "");
  const monthlyRecurringDonationSection = useOnlyOnlineRecurringDonation({
    ...monthlyRecurringDonationState,
    onChange: (val) => {
      setMonthtlyRecurringDonationState(val);
      setErrors([]);
      setWarnings([]);
    },
  });

  const [allTypeRecurringState, setAllTypeRecurring] = useState(
    ALLTYPE_MONTHLY_RECURRING_DONATION_DEFAULT_STATE
  );
  const allTypeMonthlyRecurringDonationSection = useMonthlyRecurringDonation({
    ...allTypeRecurringState,
    isOfflinePage: isOffline,
    onChange: (val) => {
      setAllTypeRecurring(val);
      setErrors([]);
      setWarnings([]);
    },
  });

  const [offlineDonationRecurringState, setOfflineDonationState] = useState(
    enableRecurringOffline ? OFFLINE_RECURRING_DONATION_DEFAULT_STATE : ""
  );
  const offlineDonationRecurringSection = useOfflineDonation({
    ...offlineDonationRecurringState,
    onChange: (val) => {
      setOfflineDonationState(val);
      setErrors([]);
      setWarnings([]);
    },
  });

  const donationConfDetails = DonationConfProps(formDonationId);
  const [donorInfo, setDonorInfo] = useState({});
  const [loading, setLoading] = useState(false);

  const [editDonorField, setEditDonorField] = useState(false);
  const { numProfiles } = useDonorProfiles(country);

  const { conf } = useDonationConf();

  const [trk, setTrk] = usePersist("donation-trk", {});
  const onSuccess = useCallback(
    (d) => {
      setTrk({
        ...trk,
        [d.donation.payment.trackingId]: d.donation.userEmailAddress,
      });
    },
    [trk, setTrk]
  );

  const containerStyle = {
    mb: 1,
    borderBottom: `thin ${theme.colors.primary} solid`,
  };

  useEffect(() => {
    // Fix for scroll down while focusing on form fields in mobile (IOS and Android)
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const systemInfo = useMemo(
    () => ({
      donation_id: formDonationId,
      projects: [projectId],
      packageInfo: packageDescription,
      paymentSuccessUrl,
      paymentFailureUrl,
    }),
    [formDonationId, projectId, packageDescription]
  );

  const resetDonorInfo = useCallback(() => {
    setDonorInfo(initialProfile);
    setErrors([]);
    setWarnings([]);
  }, [initialProfile]);

  const setDonorInfoAndTriggerPay = useCallback(
    (p) => {
      setDonorInfo(p);
      setEditDonorField(false);

      if (p) {
        doValidateSubmit({
          donationServiceConfig,
          country,
          systemInfo,
          donorInfo: p,
          amount: toAmount,
          currency: to,
          onErrors: setErrors,
          onWarnings: setWarnings,
          setLoading,
          onSuccess,
          monthlyRecurringDonationState,
          offlineDonationRecurringState,
          allTypeRecurringState,
          overrideWarnings: [],
          donationConfDetails,
          conf,
        });
      }
    },
    [toAmount, to, country, onSuccess, systemInfo]
  );

  const validateAndSubmit = useCallback(() => {
    doValidateSubmit({
      donationServiceConfig,
      country,
      systemInfo,
      donorInfo,
      amount: toAmount,
      currency: to,
      onErrors: setErrors,
      onWarnings: setWarnings,
      setLoading,
      onSuccess,
      monthlyRecurringDonationState,
      offlineDonationRecurringState,
      allTypeRecurringState,
      overrideWarnings: [],
      donationConfDetails,
      conf,
    });
  }, [
    donorInfo,
    toAmount,
    to,
    setErrors,
    setWarnings,
    setLoading,
    onSuccess,
    monthlyRecurringDonationState,
    offlineDonationRecurringState,
    allTypeRecurringState,
    country,

    systemInfo,
  ]);

  const validateAndSubmitOW = useCallback(() => {
    doValidateSubmit({
      donationServiceConfig,
      country,
      systemInfo,
      donorInfo,
      amount: toAmount,
      currency: to,
      onErrors: setErrors,
      onWarnings: setWarnings,
      setLoading,
      onSuccess,
      monthlyRecurringDonationState,
      offlineDonationRecurringState,
      allTypeRecurringState,
      overrideWarnings: ["warning-in-pan", "warning-in-identity"],
      donationConfDetails,
      conf,
    });
  }, [
    donorInfo,
    toAmount,
    to,
    setErrors,
    setWarnings,
    setLoading,
    onSuccess,
    country,
    monthlyRecurringDonationState,
    offlineDonationRecurringState,
    allTypeRecurringState,
    systemInfo,
  ]);

  // profileModes = ["restrictions", "selection-box", "editor-box", "warnings-box"];

  const restrictions = checkRestrictions({
    conf,
    country,
    amount: toAmount,
    donorInfo,
    donationId: formDonationId,
  });

  let show = "";
  if (restrictions.length > 0) {
    show = "restrictions";
  } else if (numProfiles > 0 && !editDonorField && warnings.length === 0) {
    show = "selection-box";
  } else if (!!editDonorField || (numProfiles === 0 && warnings.length === 0)) {
    show = "editor-box";
  } else {
    show = "warnings-box";
  }

  const openInfoEditor = useCallback(() => {
    setErrors([]);
    setWarnings([]);
    setEditDonorField("pan");
  }, []);

  const ButtonPay = useCallback(
    ({ extraText, ...restProps }) => (
      <div>
        <Button sx={{ ...theme.buttons.variant1 }} {...restProps}>
          {" "}
          {loading && <Spinner size={16} sx={{ color: "text" }} />}
          {isOffline ? "Confirm the details" : "Proceed"}
        </Button>
        {enableRecurring && !isOffline && country === "in" && (
          <Box
            sx={{
              color: "text",
              textAlign: "center",
              margin: "5px 0 10px 0",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            Note: By Proceeding you will be setting up the ECS subscription with
            your Bank
          </Box>
        )}
        <Tos country={country} sx={{ color: "text", fontWeight: "600" }} />
      </div>
    ),
    [to, toAmount, loading, country, theme.buttons.variant1]
  );

  const AfterSelection = useCallback(
    () => (
      <div sx={{ mt: 5 }}>
        {errors.length > 0 && (
          <div sx={theme.messages.variantEW}>
            <div sx={{ color: "errortext" }}>
              <Grid gap={1} columns={["1fr 7fr"]}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FontAwesomeIcon
                    icon="exclamation-circle"
                    sx={{ fontSize: "25px" }}
                  />
                </Box>
                <Box>
                  <Grid gap={0}>
                    <Box>
                      <h4 sx={{ mb: 0 }}>
                        <b>Form Errors.</b>
                      </h4>
                    </Box>
                    <Box>
                      <h5>Please fix to continue!</h5>
                    </Box>
                  </Grid>
                </Box>
              </Grid>
            </div>
            <ErrorsOrWarnings errors={errors} warnings={warnings} />
          </div>
        )}
        {donorInfo.name && (
          <ButtonPay
            disabled={loading || errors.length > 0}
            onClick={validateAndSubmit}
          />
        )}
      </div>
    ),
    [
      errors,
      warnings,
      loading,
      validateAndSubmit,
      donorInfo,
      theme.messages.variantEW,
    ]
  );

  return (
    <Container sx={{ maxWidth: "320px", pb: 3, margin: "0 auto" }}>
      <div sx={{ mb: 3 }}>
        <AmountSelector
          country={country}
          currency={initialCurrency}
          initialAmount={initialAmount}
          initialCurrency={initialCurrency}
          setAmount={(x) => {
            setToAmount(x);
            setErrors([]);
            setWarnings([]);
          }}
          autoFocusName={editDonorField || "amount"}
        />
        {enableRecurring && enableRecurringOffline && (
          <AllTypeRecurringDonation
            {...allTypeMonthlyRecurringDonationSection}
            org={org}
            removeAccountType={removeAccountType}
          />
        )}
        {enableRecurring && !enableRecurringOffline && (
          <OnlineRecurringDonation
            {...monthlyRecurringDonationSection}
            country={country}
            org={org}
            removeAccountType={removeAccountType}
          />
        )}
        {enableRecurringOffline && !enableRecurring && (
          <OfflineRecurringDonations {...offlineDonationRecurringSection} />
        )}
      </div>
      <RestrictionsWrapper
        show={show}
        country={country}
        donorInfo={donorInfo}
        resetDonorInfo={resetDonorInfo}
        restrictions={restrictions}
        meditationPlaceFieldAvl={meditationPlaceFieldAvl}
      />
      {show === "selection-box" && (
        <ProfileSelector
          containerStyle={containerStyle}
          country={country}
          initProfile={initialProfile}
          donorInfo={donorInfo}
          setDonorInfo={setDonorInfo}
          resetDonorInfo={resetDonorInfo}
          AfterSelection={AfterSelection}
          enableLastname={enableLastname}
          showOtherCitizen={showOtherCitizen}
          enableSignInOption={enableSignInOption}
          donationAmount={toAmount}
          meditationPlaceFieldAvl={meditationPlaceFieldAvl}
        />
      )}

      {show === "editor-box" && (
        <div>
          <div sx={{ color: "secondary", mb: 1 }}>Donor Information</div>
          <EditProfile
            enableLastname={enableRecurring && enableLastname}
            country={country}
            ButtonPay={ButtonPay}
            value={editDonorField ? donorInfo : initialProfile}
            setValue={setDonorInfoAndTriggerPay}
            containerStyle={containerStyle}
            autoFocusName={editDonorField || "name"}
            showOtherCitizen={showOtherCitizen}
            enableSignInOption={enableSignInOption}
            donationAmount={toAmount}
            meditationPlaceFieldAvl={meditationPlaceFieldAvl}
          />
        </div>
      )}
      <ErrorsOrWarningsWrapper
        show={show}
        openInfoEditor={openInfoEditor}
        ButtonPay={ButtonPay}
        validateAndSubmitOW={validateAndSubmitOW}
        country={country}
        donorInfo={donorInfo}
        resetDonorInfo={resetDonorInfo}
        AfterSelection={AfterSelection}
        errors={errors}
        warnings={warnings}
        initialCurrency={initialCurrency}
        toAmount={toAmount}
        loading={loading}
        validateAndSubmit={validateAndSubmit}
        meditationPlaceFieldAvl={meditationPlaceFieldAvl}
      />
    </Container>
  );
};

RecurringWidget.propTypes = {
  formDonationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  initialAmount: PropTypes.number,
  initialCurrency: PropTypes.string,
  projectId: PropTypes.string,
  packageDescription: PropTypes.string,
  country: PropTypes.string,
  initialProfile: PropTypes.shape({}),
  enableRecurring: PropTypes.bool,
  enableRecurringOffline: PropTypes.bool,
  enableLastname: PropTypes.bool,
  paymentSuccessUrl: PropTypes.string.isRequired,
  paymentFailureUrl: PropTypes.string.isRequired,
  showOtherCitizen: PropTypes.bool,
  enableSignInOption: PropTypes.bool,
  org: PropTypes.string,
  removeAccountType: PropTypes.bool,
  meditationPlaceFieldAvl: PropTypes.bool,
};

RecurringWidget.defaultProps = {
  initialAmount: 0,
  initialCurrency: "INR",
  projectId: "",
  packageDescription: "",
  country: "in",
  initialProfile: {},
  enableRecurring: false,
  enableRecurringOffline: false,
  enableLastname: false,
  showOtherCitizen: true,
  enableSignInOption: true,
  org: "",
  removeAccountType: false,
  meditationPlaceFieldAvl: false,
};
export default RecurringWidget;
